/**
 * Purpose: Main gameplay screen — orchestrates the turn phase state machine and period header.
 * Owns: Local turn phase state (QUESTION → ANSWER_FEEDBACK → DICE_ROLL → BOARD_VIEW →
 *       TURN_TRANSITION), period header display, phase-specific component rendering,
 *       local used-question tracking, schedule drawer toggle, end game detection.
 * Notes: This is the primary screen during play (CS-17: orchestrator only).
 *        The turn phase state machine is LOCAL to this component. Real game state
 *        mutations (wrong answer, correct answer + dice, next turn) are dispatched
 *        to GameContext at the appropriate phase boundaries.
 *        Pure Classroom mode skips DICE_ROLL and BOARD_VIEW phases entirely —
 *        answers go directly from FEEDBACK to TURN_TRANSITION.
 *        Used question IDs are tracked locally so questions don't repeat within a session.
 *        Phase sub-components live in GameplayPhaseContent.tsx (CS-15 split).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { Scoreboard } from '../components/game/Scoreboard';
import { ClassroomView } from '../components/classroom/ClassroomView';
import { ScheduleDrawer } from '../components/game/ScheduleDrawer';
import { ClassroomPreviewOverlay } from '../components/game/ClassroomPreviewOverlay';
import {
  QuestionPhaseContent,
  FeedbackPhaseContent,
  DiceRollPhaseContent,
  TurnTransitionContent,
} from './GameplayPhaseContent';
import { BehaviorDock } from '../components/host/BehaviorDock';
import { selectQuestion } from '../engine/questionSelector';
import { getCurrentSubjectForPlayer } from '../engine/seatProgression';
import { rollDice } from '../engine/diceRoller';
import type { DiceValue } from '../engine/diceRoller';
import type { Question, Category } from '../types/question';

/* Turn phases — Correct: QUESTION→FEEDBACK→DICE→BOARD→TRANSITION; Wrong: skips dice+board */
type TurnPhase = 'QUESTION' | 'ANSWER_FEEDBACK' | 'DICE_ROLL' | 'BOARD_VIEW' | 'TURN_TRANSITION';

/* Subject emoji icons for the period header (CS-21). */
const SUBJECT_EMOJIS: Record<Category | 'Final Exam', string> = {
  Math: '\u{1F9EE}',
  Science: '\u{1F52C}',
  History: '\u{1F4DC}',
  English: '\u{1F4D6}',
  Art: '\u{1F3A8}',
  Geography: '\u{1F30D}',
  Music: '\u{1F3B5}',
  Sports: '\u{1F3C8}',
  'Final Exam': '\u{1F393}',
};

const TURN_TRANSITION_MS = 1500;          // Auto-transition duration (CS-20)
const GAME_OVER_NAV_DELAY_MS = 2000;      // Delay before results nav on game over (CS-20)

export function GameplayPage() {
  const navigate = useNavigate();
  const { state, pack, markQuestionUsed, applyWrongAnswer, applyCorrectAnswerWithDice, nextTurn, incrementHelpCount, applyBehaviorDock, applyPureClassroomAnswer } =
    useGame();

  const activePlayer = state.players[state.currentPlayerIndex];
  const isSoloMode = state.players.length === 1;
  const isPureClassroom = state.gameMode === 'pureClassroom';

  /* --- Local turn phase state --- */
  const [turnPhase, setTurnPhase] = useState<TurnPhase>('QUESTION');
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [diceResult, setDiceResult] = useState<DiceValue | null>(null);

  /* --- Question state for the current turn --- */
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  /* --- Dice rolling animation flag --- */
  const [isDiceRolling, setIsDiceRolling] = useState(false);

  /* --- Used question IDs tracked locally across the whole game session --- */
  const usedQuestionIdsRef = useRef<Set<string>>(new Set());

  /* --- Points earned this turn (for ClassroomView) --- */
  const [pointsEarnedThisTurn, setPointsEarnedThisTurn] = useState(0);

  /* --- Schedule drawer visibility --- */
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);

  /* --- Classroom preview overlay (view-only, not part of turn flow) --- */
  const [isClassroomPreviewOpen, setIsClassroomPreviewOpen] = useState(false);

  /* --- Behavior dock panel (host-only parent control) --- */
  const [isBehaviorDockOpen, setIsBehaviorDockOpen] = useState(false);

  /* --- Teacher's Desk hint state (reset each turn) --- */
  const [hintUsed, setHintUsed] = useState(false);
  const [eliminatedOption, setEliminatedOption] = useState<number | null>(null);

  /* End game detection — navigate to results unless board view animation is playing */
  const isGameOver = state.gamePhase === 'results';

  useEffect(() => {
    if (isGameOver && turnPhase !== 'BOARD_VIEW' && turnPhase !== 'DICE_ROLL') {
      navigate('/results');
    }
  }, [isGameOver, turnPhase, navigate]);

  /* Question selection — pick a question when entering QUESTION phase */
  useEffect(() => {
    if (turnPhase !== 'QUESTION' || !activePlayer || !pack) return;

    const category = getCurrentSubjectForPlayer(activePlayer);
    const question = selectQuestion(
      pack.questions,
      activePlayer.ageGroup,
      activePlayer.tier,
      category,
      usedQuestionIdsRef.current,
    );

    if (question) {
      usedQuestionIdsRef.current.add(question.id);
      markQuestionUsed(question.id);
    }

    setCurrentQuestion(question);
    setSelectedAnswer(null);
  }, [turnPhase, activePlayer?.id, activePlayer?.currentPeriod, pack, markQuestionUsed]);

  /* Auto-transition: TURN_TRANSITION → QUESTION (solo = instant, multiplayer = 1.5s) */
  useEffect(() => {
    if (turnPhase !== 'TURN_TRANSITION') return;

    const startNextTurn = () => {
      setLastAnswerCorrect(null);
      setDiceResult(null);
      setCurrentQuestion(null);
      setSelectedAnswer(null);
      setPointsEarnedThisTurn(0);
      setHintUsed(false);
      setEliminatedOption(null);
      setTurnPhase('QUESTION');
    };

    /* Solo mode skips the "next player" interstitial entirely */
    if (isSoloMode) {
      startNextTurn();
      return;
    }

    const timer = setTimeout(startNextTurn, TURN_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [turnPhase, isSoloMode]);

  /** Player taps "Teacher's Desk" to request a hint during the QUESTION phase. */
  const handleRequestHelp = useCallback(() => {
    if (!currentQuestion || hintUsed || selectedAnswer !== null) return;

    setHintUsed(true);

    /* For multiple-choice: eliminate one random incorrect answer */
    if (currentQuestion.type === 'multiple-choice') {
      const wrongIndices = currentQuestion.options
        .map((_: string, i: number) => i)
        .filter((i: number) => i !== currentQuestion.correctAnswer);
      const randomWrong = wrongIndices[Math.floor(Math.random() * wrongIndices.length)];
      setEliminatedOption(randomWrong);
    }
  }, [currentQuestion, hintUsed, selectedAnswer]);

  /** Player taps an answer option during the QUESTION phase. */
  const handleAnswerSelected = useCallback(
    (answerIndex: number) => {
      if (!currentQuestion || selectedAnswer !== null) return;

      const isCorrect = answerIndex === currentQuestion.correctAnswer;
      setSelectedAnswer(answerIndex);
      setLastAnswerCorrect(isCorrect);

      /* If correct and hint was used, increment questionsWithHelp before
         the answer processes (scoring knockdown). */
      if (isCorrect && hintUsed) {
        incrementHelpCount();
      }

      if (isPureClassroom) {
        /* Pure Classroom: process answer immediately (no dice, no seats) */
        applyPureClassroomAnswer(isCorrect);
      } else if (!isCorrect) {
        /* Standard modes: wrong answers processed now; correct waits for dice */
        applyWrongAnswer();
      }

      /* Short delay to show the selected state, then transition to feedback */
      setTimeout(() => {
        setTurnPhase('ANSWER_FEEDBACK');
      }, 400);
    },
    [currentQuestion, selectedAnswer, applyWrongAnswer, hintUsed, incrementHelpCount, isPureClassroom, applyPureClassroomAnswer],
  );

  /** Player taps "Continue" on the feedback screen — advance to dice roll or next turn.
   *  Pure Classroom always goes to TURN_TRANSITION (no dice, no board view).
   *  Wrong answers (all modes) and Pure Classroom answers advance the turn here
   *  so that currentPlayerIndex is correct when TURN_TRANSITION renders. */
  const handleFeedbackContinue = useCallback(() => {
    if (isPureClassroom) {
      /* Pure Classroom: no dice or board — advance turn and show transition */
      nextTurn();
      setTurnPhase('TURN_TRANSITION');
      return;
    }
    if (lastAnswerCorrect) {
      setTurnPhase('DICE_ROLL');
    } else {
      /* Wrong answer: advance turn now (correct answers advance in handleNextTurn) */
      nextTurn();
      setTurnPhase('TURN_TRANSITION');
    }
  }, [lastAnswerCorrect, isPureClassroom, nextTurn]);

  /** Dice roll animation starts when entering the DICE_ROLL phase. */
  useEffect(() => {
    if (turnPhase !== 'DICE_ROLL') return;

    const value = rollDice();
    setDiceResult(value);
    setIsDiceRolling(true);
  }, [turnPhase]);

  /** DiceRoll component signals animation complete — process the correct answer. */
  const handleDiceRollComplete = useCallback(() => {
    if (diceResult === null) return;

    setIsDiceRolling(false);
    setPointsEarnedThisTurn(diceResult);
    applyCorrectAnswerWithDice(diceResult);
    setTurnPhase('BOARD_VIEW');
  }, [diceResult, applyCorrectAnswerWithDice]);

  /** User taps "Next Turn" from the board view.
   *  If the game is already over (a win condition was met during this turn),
   *  navigate to results after a short celebration delay instead of advancing. */
  const handleNextTurn = useCallback(() => {
    if (isGameOver) {
      /* Let the graduation celebration linger briefly, then go to results */
      setTimeout(() => navigate('/results'), GAME_OVER_NAV_DELAY_MS);
      return;
    }

    nextTurn();
    setTurnPhase('TURN_TRANSITION');
  }, [nextTurn, isGameOver, navigate]);

  /* --- Guard: no players --- */
  if (!activePlayer) {
    return (
      <div className="chalkboard-bg" style={styles.page}>
        <p style={styles.emptyText}>No players in the game. Go back to setup.</p>
      </div>
    );
  }

  /* --- Read period/subject data for the header --- */
  const periodIndex = activePlayer.currentPeriod - 1;
  const currentPeriodEntry = activePlayer.schedule[periodIndex];
  const currentSubject = currentPeriodEntry?.subject ?? 'Unknown';
  const subjectEmoji = SUBJECT_EMOJIS[currentSubject as keyof typeof SUBJECT_EMOJIS] ?? '';
  const currentProgress = activePlayer.classProgress[periodIndex];
  /* Pure Classroom: show questions answered; Standard: show seat position */
  const progressCurrent = isPureClassroom
    ? (currentProgress?.questionsAnswered ?? 0)
    : (currentProgress?.currentSeat ?? 0);
  const progressTotal = currentPeriodEntry?.classSize.seats ?? 0;

  return (
    <div className="chalkboard-bg" style={styles.page}>
      {/* Period header — persistent across all turn phases except BOARD_VIEW */}
      {turnPhase !== 'BOARD_VIEW' && (
        <PeriodHeader
          periodNumber={activePlayer.currentPeriod}
          subject={currentSubject}
          subjectEmoji={subjectEmoji}
          progressCurrent={progressCurrent}
          progressTotal={progressTotal}
          isPureClassroom={isPureClassroom}
          playerName={activePlayer.name}
          playerColor={activePlayer.color}
          isSoloMode={isSoloMode}
          onScheduleOpen={() => setIsScheduleOpen(true)}
          onClassroomOpen={() => setIsClassroomPreviewOpen(true)}
          onBehaviorDockOpen={() => setIsBehaviorDockOpen(true)}
        />
      )}

      {/* Wood-framed game area with phase-specific content */}
      {turnPhase !== 'BOARD_VIEW' && (
        <div className="wood-frame" style={styles.frameWrap}>
          <div style={styles.gameArea}>
            {turnPhase === 'QUESTION' && currentQuestion && (
              <QuestionPhaseContent
                question={currentQuestion}
                selectedAnswer={selectedAnswer}
                onSelectAnswer={handleAnswerSelected}
                hintUsed={hintUsed}
                eliminatedOption={eliminatedOption}
                onRequestHelp={handleRequestHelp}
              />
            )}

            {turnPhase === 'QUESTION' && !currentQuestion && (
              <div style={styles.noQuestionsWrap}>
                <p className="chalk-text" style={styles.noQuestionsText}>
                  No more questions available. Great job!
                </p>
              </div>
            )}

            {turnPhase === 'ANSWER_FEEDBACK' && currentQuestion && (
              <FeedbackPhaseContent
                wasCorrect={lastAnswerCorrect}
                question={currentQuestion}
                selectedAnswer={selectedAnswer}
                onContinue={handleFeedbackContinue}
              />
            )}

            {turnPhase === 'DICE_ROLL' && (
              <DiceRollPhaseContent
                diceValue={diceResult}
                isRolling={isDiceRolling}
                onRollComplete={handleDiceRollComplete}
              />
            )}

            {turnPhase === 'TURN_TRANSITION' && !isSoloMode && (
              <TurnTransitionContent nextPlayerName={activePlayer?.name ?? ''} />
            )}
          </div>
        </div>
      )}

      {/* BOARD_VIEW phase renders ClassroomView as a full-screen takeover */}
      {turnPhase === 'BOARD_VIEW' && (
        <ClassroomView
          onNextTurn={handleNextTurn}
          wasCorrect={lastAnswerCorrect}
          pointsEarned={pointsEarnedThisTurn}
        />
      )}

      {/* Bottom scoreboard — hidden during BOARD_VIEW (ClassroomView has its own) */}
      {turnPhase !== 'BOARD_VIEW' && (
        <Scoreboard players={state.players} activePlayerId={activePlayer.id} />
      )}

      {/* Schedule drawer — shows the active player's full class schedule */}
      <ScheduleDrawer
        isOpen={isScheduleOpen}
        onClose={() => setIsScheduleOpen(false)}
        player={activePlayer}
      />

      {/* Classroom preview — view-only overlay of the desk grid */}
      {isClassroomPreviewOpen && (
        <ClassroomPreviewOverlay onClose={() => setIsClassroomPreviewOpen(false)} />
      )}

      {/* Behavior dock — host-only panel for docking points for bad behavior */}
      <BehaviorDock
        isOpen={isBehaviorDockOpen}
        onClose={() => setIsBehaviorDockOpen(false)}
        players={state.players}
        activePlayerId={activePlayer.id}
        onApplyDock={applyBehaviorDock}
      />
    </div>
  );
}

function PeriodHeader({
  periodNumber,
  subject,
  subjectEmoji,
  progressCurrent,
  progressTotal,
  isPureClassroom,
  playerName,
  playerColor,
  isSoloMode,
  onScheduleOpen,
  onClassroomOpen,
  onBehaviorDockOpen,
}: {
  periodNumber: number;
  subject: string;
  subjectEmoji: string;
  progressCurrent: number;
  progressTotal: number;
  isPureClassroom: boolean;
  playerName: string;
  playerColor: string;
  isSoloMode: boolean;
  onScheduleOpen: () => void;
  onClassroomOpen: () => void;
  onBehaviorDockOpen: () => void;
}) {
  /* Pure Classroom shows question count; standard modes show seat position */
  const progressLabel = isPureClassroom
    ? `Question ${progressCurrent} of ${progressTotal}`
    : `Seat ${progressCurrent} of ${progressTotal}`;

  return (
    <div style={styles.periodHeader}>
      {/* Period and subject line with utility buttons */}
      <div style={styles.periodRow}>
        <button
          style={styles.headerIconButton}
          onClick={onClassroomOpen}
          aria-label="View classroom"
        >
          {'\uD83C\uDFEB'}
        </button>
        <span className="chalk-text-bright" style={styles.periodTitle}>
          {subjectEmoji} Period {periodNumber}: {subject}
        </span>
        <button
          style={styles.headerIconButton}
          onClick={onScheduleOpen}
          aria-label="View schedule"
        >
          {'\uD83D\uDCCB'}
        </button>
      </div>

      {/* Progress, player info, and behavior dock trigger */}
      <div style={styles.periodSubRow}>
        <span className="chalk-text" style={styles.seatText}>
          {progressLabel}
        </span>

        {/* Player name with color dot — hidden in solo since it's obvious */}
        {!isSoloMode && (
          <span style={styles.playerBadge}>
            <span
              style={{ ...styles.playerDot, backgroundColor: playerColor }}
              aria-hidden="true"
            />
            <span className="chalk-text" style={styles.playerNameText}>
              {playerName}
            </span>
          </span>
        )}

        {/* Behavior dock trigger — host-only, subtle placement */}
        <button
          style={styles.dockTrigger}
          onClick={onBehaviorDockOpen}
          aria-label="Open behavior dock controls"
          title="Behavior Dock"
        >
          {'\u26A0\uFE0F'}
        </button>
      </div>
    </div>
  );
}

/* Styles (CS-22: theme tokens only) */
const styles: Record<string, React.CSSProperties> = {
  page: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  /* Period header — chalkboard strip at the top */
  periodHeader: {
    padding: '0.75rem 1rem 0.5rem',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderBottom: '3px solid var(--accent-wood)',
    textAlign: 'center' as const,
  },
  periodRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.5rem',
  },
  periodTitle: {
    fontSize: '1.3rem',
    letterSpacing: '0.04em',
  },
  headerIconButton: {
    fontSize: '1.2rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    opacity: 0.7,
    lineHeight: 1,
  },
  periodSubRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '0.25rem',
  },
  seatText: {
    fontSize: '0.85rem',
    opacity: 0.7,
  },
  playerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
  },
  playerDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  playerNameText: {
    fontSize: '0.85rem',
    opacity: 0.85,
  },
  /* Behavior dock trigger button — subtle, tucked into the sub-row */
  dockTrigger: {
    fontSize: '0.9rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.2rem',
    opacity: 0.5,
    lineHeight: 1,
    minHeight: '32px',
    minWidth: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* Wood frame and game area */
  frameWrap: {
    flex: 1,
    margin: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  gameArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '1rem 0.5rem',
  },
  /* "No more questions" fallback */
  noQuestionsWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    padding: '1rem',
    textAlign: 'center' as const,
    width: '100%',
  },
  noQuestionsText: {
    fontSize: '1.2rem',
    lineHeight: 1.4,
  },
  /* Empty state */
  emptyText: {
    color: 'var(--muted)',
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.1rem',
    fontFamily: 'var(--font-family)',
  },
};
